/**
 * Admin → Purchases (Phase C)
 *
 * Aggregated purchasing list grouped by product, derived from scheduled
 * deliveries in the chosen date range. Includes CSV export.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Download, ShoppingCart, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoPlus(days) { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }

async function adminGet(path) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, { headers: { Authorization: t ? `Bearer ${t}` : '' } });
  if (!res.ok) throw new Error((await res.text()) || `${res.status}`);
  return res.json();
}

export default function AdminPurchases() {
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoPlus(30));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ from, to });
      const d = await adminGet(`/api/admin/purchases?${qs.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadCSV = async () => {
    const t = getToken();
    const qs = new URLSearchParams({ from, to, fmt: 'csv' });
    const res = await fetch(`${API}/api/admin/purchases?${qs.toString()}`, { headers: { Authorization: t ? `Bearer ${t}` : '' } });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `purchases_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5" data-testid="admin-purchases-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">Operations · Purchases</p>
          <h1 className="font-display text-3xl text-[#05223D]">Purchases</h1>
          <p className="text-sm text-[#465B70] mt-1">Aggregated product purchasing list for the selected horizon. Use the CSV export for your suppliers.</p>
        </div>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#465B70]">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl" data-testid="purchases-from" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#465B70]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl" data-testid="purchases-to" />
          </div>
          <Button onClick={load} disabled={loading} className="h-10 rounded-xl bg-[#0A4D8C] hover:bg-[#053B70]" data-testid="purchases-refresh">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button onClick={downloadCSV} variant="outline" className="h-10 rounded-xl" data-testid="purchases-export">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </CardContent>
      </Card>

      {!data ? (
        <div className="flex items-center gap-2 text-[#465B70]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : data.rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-[#465B70] bg-white rounded-2xl border border-[#0A4D8C1A]">
          <ShoppingCart className="h-8 w-8 mb-2" />
          Nothing to plan in this date range.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#0A4D8C1A] bg-white overflow-hidden" data-testid="purchases-table-wrap">
          <div className="px-4 py-3 border-b border-[#0A4D8C14] text-sm text-[#465B70]">
            <strong className="text-[#05223D]">Total qty:</strong> {data.total?.toFixed?.(2) || 0}
            <span className="ml-4"><strong className="text-[#05223D]">Lines:</strong> {data.rows.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-wider text-[#465B70]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Product</th>
                <th className="text-right px-4 py-2 font-bold">Total qty</th>
                <th className="text-right px-4 py-2 font-bold">Orders</th>
                <th className="text-right px-4 py-2 font-bold">Customers</th>
                <th className="text-left px-4 py-2 font-bold">First date</th>
                <th className="text-left px-4 py-2 font-bold">Last date</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-[#0A4D8C14] hover:bg-[#FDFBF7]" data-testid={`purchases-row-${i}`}>
                  <td className="px-4 py-2 font-bold text-[#05223D]">{r.product_name || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.total_qty.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.orders}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.customers}</td>
                  <td className="px-4 py-2 text-[#465B70]">{r.earliest?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-[#465B70]">{r.latest?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
